/**
 * Points an in-page link at the anchor the sanitiser renamed.
 *
 * `rehype-sanitize` prefixes every `id` and `name` with a clobber prefix, so
 * copy cannot overwrite a page global such as `window.name`. An in-page link
 * `href="#spec"` then no longer reaches `<a name="spec">`. GitHub keeps the
 * prefix and resolves such links in its page script; this plugin resolves
 * them in the tree, so the link works without script.
 *
 * A fragment-only `href` gets the prefix when the same document holds an `id`
 * or `name` with the prefixed value. A link to an anchor the document does not
 * hold keeps its `href`, and so does every `href` that is not fragment-only.
 * The fragment is compared decoded, and the rewritten `href` keeps the
 * original encoding.
 */

type HastElement = {
  type: 'element';
  tagName: string;
  properties: Record<string, unknown>;
  children: unknown[];
};

const isElement = (node: unknown): node is HastElement =>
  typeof node === 'object' &&
  node !== null &&
  'type' in node &&
  node.type === 'element' &&
  'tagName' in node &&
  typeof node.tagName === 'string' &&
  'properties' in node &&
  typeof node.properties === 'object' &&
  node.properties !== null &&
  'children' in node &&
  Array.isArray(node.children);

const childrenOf = (node: unknown): readonly unknown[] =>
  typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children)
    ? node.children
    : [];

const elementsOf = (tree: unknown): HastElement[] => {
  const found: HastElement[] = [];
  const walk = (node: unknown): void => {
    if (isElement(node)) found.push(node);
    for (const child of childrenOf(node)) walk(child);
  };
  walk(tree);
  return found;
};

/** A malformed escape such as `%E0%A4%A` makes `decodeURIComponent` throw. */
const decodeFragment = (fragment: string): string => {
  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
};

export const rehypeInPageAnchors = (clobberPrefix: string) => (tree: unknown) => {
  const elements = elementsOf(tree);
  const targets = new Set(
    elements.flatMap(({ properties }) =>
      [properties.id, properties.name].filter((value): value is string => typeof value === 'string')
    )
  );

  for (const { tagName, properties } of elements) {
    const href = properties.href;
    if (tagName !== 'a' || typeof href !== 'string' || !href.startsWith('#') || href === '#') {
      continue;
    }

    const fragment = href.slice(1);
    if (targets.has(`${clobberPrefix}${decodeFragment(fragment)}`)) {
      properties.href = `#${clobberPrefix}${fragment}`;
    }
  }
};
